// Environment configuration for Issue Management domains
// Similar to the structure used in env-config.json

export type Environment = 'int' | 'qa' | 'stage' | 'prod';

export interface IssueManagementEnvironmentConfig {
  issueManagementDomain: string;
  tcpsApiDomain: string;
  userApiDomain: string;
  followApiDomain: string;
  ecomApiDomain: string;
  bcfDomain: string;
  batchApiDomain: string;
  enableIssueAcl: boolean;
}

// Regional URL patterns - these get combined with environment
export interface RegionalEndpoints {
  tcpsApi: string;
  userApi: string;
  issueApi: string;
  bcfApi: string;
  // Add other regional endpoints as needed
}

// Regional mapping based on project location
export const REGIONAL_ENDPOINTS: Record<string, RegionalEndpoints> = {
  northAmerica: {
    tcpsApi: 'app.connect.trimble.com/tc/api/2.0',
    userApi: 'user.connect.trimble.com/v1',
    issueApi: 'app.connect.trimble.com/v1',
    bcfApi: 'open11.connect.trimble.com',
  },
  europe: {
    tcpsApi: 'app21.connect.trimble.com/tc/api/2.0',
    userApi: 'user.connect.trimble.com/v1',
    issueApi: 'app21.connect.trimble.com/v1',
    bcfApi: 'open21.connect.trimble.com',
  },
  asia: {
    tcpsApi: 'app31.connect.trimble.com/tc/api/2.0',
    userApi: 'user.connect.trimble.com/v1',
    issueApi: 'app31.connect.trimble.com/v1',
    bcfApi: 'open31.connect.trimble.com',
  },
  australia: {
    tcpsApi: 'app32.connect.trimble.com/tc/api/2.0',
    userApi: 'user.connect.trimble.com/v1',
    issueApi: 'app32.connect.trimble.com/v1',
    bcfApi: 'open32.connect.trimble.com',
  },
  unitedKingdom: {
    tcpsApi: 'app22.connect.trimble.com/tc/api/2.0',
    userApi: 'user.connect.trimble.com/v1',
    issueApi: 'app22.connect.trimble.com/v1',
    bcfApi: 'open22.connect.trimble.com',
  },
};

// Helper function to build regional URL
// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 29 > 15 allowed; refactor later
export function buildRegionalUrl(
  environment: Environment,
  region: string | null,
  baseEndpoint: string
): string {
  // If no region yet, use default environment-based URL
  if (!region) {
    const config = ISSUE_MANAGEMENT_ENV_CONFIG[environment];
    const fallbackUrl =
      baseEndpoint === 'tcps'
        ? config.tcpsApiDomain
        : baseEndpoint === 'user'
          ? config.userApiDomain
          : baseEndpoint === 'issue'
            ? config.issueManagementDomain
            : baseEndpoint === 'follow'
              ? config.followApiDomain
              : baseEndpoint === 'bcf'
                ? config.bcfDomain
                : config.ecomApiDomain;
    return fallbackUrl;
  }

  // Get regional endpoint
  const regionalEndpoints = REGIONAL_ENDPOINTS[region];

  // Build environment-specific regional URL
  const envSuffix = environment === 'prod' ? '' : `${environment}.`;

  if (baseEndpoint === 'tcps') {
    // For TCPS: app21.connect.trimble.com -> app21.qa.connect.trimble.com
    const tcpsUrl = regionalEndpoints.tcpsApi.replace(
      'connect.trimble.com',
      `${envSuffix}connect.trimble.com`
    );
    const finalUrl = `https://${tcpsUrl}`;
    return finalUrl;
  } else if (baseEndpoint === 'user') {
    // For User API: user-api-eu.connect.trimble.com -> user-api-eu.qa.connect.trimble.com
    const userUrl = regionalEndpoints.userApi.replace(
      'connect.trimble.com',
      `${envSuffix}connect.trimble.com`
    );
    const finalUrl = `https://${userUrl}`;
    return finalUrl;
  } else if (baseEndpoint === 'issue') {
    // For Issue API: app21.connect.trimble.com -> app21.qa.connect.trimble.com
    const issueUrl = regionalEndpoints.issueApi.replace(
      'connect.trimble.com',
      `${envSuffix}connect.trimble.com`
    );
    const finalUrl = `https://${issueUrl}`;
    return finalUrl;
  } else if (baseEndpoint === 'bcf') {
    // For BCF API: open21.connect.trimble.com -> open21.qa.connect.trimble.com
    const bcfUrl = regionalEndpoints.bcfApi.replace(
      'connect.trimble.com',
      `${envSuffix}connect.trimble.com`
    );
    const finalUrl = `https://${bcfUrl}`;
    return finalUrl;
  }

  // For non-regional services, use environment default
  const config = ISSUE_MANAGEMENT_ENV_CONFIG[environment];
  return baseEndpoint === 'issue'
    ? config.issueManagementDomain
    : baseEndpoint === 'follow'
      ? config.followApiDomain
      : config.ecomApiDomain;
}

export const ISSUE_MANAGEMENT_ENV_CONFIG: Record<
  Environment,
  IssueManagementEnvironmentConfig
> = {
  int: {
    issueManagementDomain: 'https://app.int.connect.trimble.com/v1',
    tcpsApiDomain: 'https://app.int.connect.trimble.com/tc/api/2.0',
    userApiDomain: 'https://user.int.connect.trimble.com/v1',
    followApiDomain: 'https://app.int.connect.trimble.com/v1',
    ecomApiDomain: 'https://ecom.int.connect.trimble.com/v1',
    bcfDomain: 'https://open11.int.connect.trimble.com',
    batchApiDomain: 'https://batch-api.int.connect.trimble.com/v1',
    enableIssueAcl: true,
  },
  qa: {
    issueManagementDomain: 'https://app.qa.connect.trimble.com/v1',
    tcpsApiDomain: 'https://app.qa.connect.trimble.com/tc/api/2.0',
    userApiDomain: 'https://user.qa.connect.trimble.com/v1',
    followApiDomain: 'https://app.qa.connect.trimble.com/v1',
    ecomApiDomain: 'https://ecom.qa.connect.trimble.com/v1',
    bcfDomain: 'https://open11.qa.connect.trimble.com',
    batchApiDomain: 'https://batch-api.qa.connect.trimble.com/v1',
    enableIssueAcl: true,
  },
  stage: {
    issueManagementDomain: 'https://app.stage.connect.trimble.com/v1',
    tcpsApiDomain: 'https://app.stage.connect.trimble.com/tc/api/2.0',
    userApiDomain: 'https://user.stage.connect.trimble.com/v1',
    followApiDomain: 'https://app.stage.connect.trimble.com/v1',
    ecomApiDomain: 'https://ecom.stage.connect.trimble.com/v1',
    bcfDomain: 'https://open11.stage.connect.trimble.com',
    batchApiDomain: 'https://batch-api.stage.connect.trimble.com/v1',
    enableIssueAcl: true,
  },
  prod: {
    issueManagementDomain: 'https://app.connect.trimble.com/v1',
    tcpsApiDomain: 'https://app.connect.trimble.com/tc/api/2.0',
    userApiDomain: 'https://user.connect.trimble.com/v1',
    followApiDomain: 'https://app.connect.trimble.com/v1',
    ecomApiDomain: 'https://ecom.connect.trimble.com/v1',
    bcfDomain: 'https://open11.connect.trimble.com',
    batchApiDomain: 'https://batch-api.connect.trimble.com/v1',
    enableIssueAcl: true,
  },
};

export const DEFAULT_ENVIRONMENT: Environment = 'stage';
